/**
 * Program Overview — Insights Page
 *
 * Answers: "Where does our security and technology program currently stand?"
 * Tabbed layout: Maturity Snapshot / Domain Health / Risk Alignment
 */

import { useState } from 'react'
import { useInsightsData } from '../hooks/useInsightsData'
import InsightCard from '../components/InsightCard'
import { MATURITY_LEVELS, getMaturityLevelByScore } from '../../../shared/utils/maturityLevels'
import DomainAnalysis from './DomainAnalysis'
import AdvisoryReportButton from '../../../shared/components/AdvisoryReportButton'

const DOMAIN_CONTEXT = {
  'Access & Identity': { strength: 'Strong identity governance reduces unauthorized access risk', weakness: 'Gaps in access controls elevate credential-based attack risk' },
  'AI Adoption': { strength: 'Awareness of AI usage supports responsible adoption', weakness: 'Unmanaged AI adoption creates data leakage and compliance risk' },
  'AI Governance': { strength: 'Formal AI governance protects against model risk and bias', weakness: 'Absence of AI governance exposes the organization to regulatory and ethical risk' },
  'Data Privacy': { strength: 'Privacy controls protect sensitive data and support regulatory compliance', weakness: 'Privacy gaps create regulatory exposure and erode customer trust' },
  'Governance & Strategy': { strength: 'Formal governance provides executive visibility and risk accountability', weakness: 'Absence of governance limits executive oversight and regulatory accountability' },
  'Incident Readiness': { strength: 'Incident response capability reduces breach impact and recovery time', weakness: 'Weak incident readiness extends recovery time and increases breach costs' },
  'Operations & Technology': { strength: 'Operational controls protect core systems and detect threats effectively', weakness: 'Operational gaps leave systems vulnerable to exploitation and outage' },
  'People & Culture': { strength: 'Security-aware culture reduces human-factor risk', weakness: 'Limited training increases susceptibility to social engineering' },
  'Risk & Incident History': { strength: 'Learning from incidents drives continuous improvement', weakness: 'Lack of incident tracking prevents learning from past events' },
  'Security & IT Program Structure': { strength: 'Defined program structure enables consistent security operations', weakness: 'Informal structure creates gaps in accountability and process consistency' },
  'Technology Environment': { strength: 'Well-managed technology reduces attack surface', weakness: 'Unmanaged technology assets create blind spots and unpatched vulnerabilities' },
  'Technology & Systems': { strength: 'Vendor oversight and system management reduce third-party risk', weakness: 'Vendor blind spots and system gaps create inherited vulnerabilities' },
  'Data Management': { strength: 'Data lifecycle management supports compliance and reduces breach scope', weakness: 'Poor data management expands breach impact and complicates regulatory response' },
  'Program & Risk Reporting': { strength: 'Security reporting enables informed executive decision-making', weakness: 'Lack of reporting leaves leadership uninformed about security posture' }
}


function ProgramOverview({ assessmentId, client }) {
  const { data, raw, loading, error } = useInsightsData(assessmentId)
  const [activeTab, setActiveTab] = useState('snapshot')

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
  if (error || !data) return <div className="text-center py-12 text-gray-500 dark:text-gray-400">Unable to load program overview. Complete a maturity assessment first.</div>

  const { programHealth, domainAnalysis } = data
  const scoreColor = getMaturityLevelByScore(programHealth.overallScore).color


  const strongest = [...(domainAnalysis.categories || [])].sort((a, b) => b.score - a.score).slice(0, 5)
  const weakest = [...(domainAnalysis.categories || [])].sort((a, b) => a.score - b.score).slice(0, 5)
  const categoryCount = domainAnalysis.categories?.length || 0

  // CMMI level + peer percentile
  const currentLevel = MATURITY_LEVELS.find(l => l.level === (programHealth.maturityLevel?.level || 1))
  const percentile = raw?.benchmarkData?.percentile ?? (programHealth.benchmark.gap != null ? Math.max(0, Math.min(100, 50 + programHealth.benchmark.gap)) : null)
  const industry = client?.industry || 'Industry'

  // Top 4 for strengths/improvements display
  const top4Strengths = [...(domainAnalysis.categories || [])].sort((a, b) => b.score - a.score).slice(0, 4)
  const top4Improvements = [...(domainAnalysis.categories || [])].sort((a, b) => a.score - b.score).slice(0, 4)

  const tabs = [
    { id: 'snapshot', label: 'Maturity Snapshot' },
    { id: 'domains', label: 'Domain Health' }
  ]

  return (
    <div className="px-1 py-2">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Program Overview</h1>
          <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
            Security maturity snapshot for {client?.name || 'this client'}
          </p>
        </div>
        <AdvisoryReportButton assessmentId={assessmentId} client={client} />
      </div>

      {/* Executive paragraph */}
      <div className="mb-6 mt-4">
        <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">
          {client?.name ? <span className="font-medium text-gray-800 dark:text-gray-200">{client.name}</span> : 'This organization'}'s
          security program scores <span className="font-semibold" style={{ color: scoreColor }}>{programHealth.overallScore}%</span> overall,
          placing it at the <span className="font-medium text-gray-800 dark:text-gray-200">{programHealth.maturityLevel?.label}</span> maturity level
          {programHealth.benchmark.gap !== 0 && (
            <> — <span className={`font-medium ${programHealth.benchmark.gap >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {Math.abs(programHealth.benchmark.gap)}% {programHealth.benchmark.gap >= 0 ? 'above' : 'below'}
            </span> {client?.industry || 'industry'} peers</>
          )}.
          {' '}Across <span className="font-medium text-gray-800 dark:text-gray-200">{categoryCount} security domains</span>,
          {weakest[0] && <> the greatest opportunity for improvement is in <span className="font-medium text-gray-800 dark:text-gray-200">{weakest[0].name}</span> ({weakest[0].score}%)</>}
          {strongest[0] && <> while <span className="font-medium text-gray-800 dark:text-gray-200">{strongest[0].name}</span> ({strongest[0].score}%) represents the strongest foundation</>}.
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200 dark:border-gray-700 mt-4">
        <nav className="flex gap-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content spacer */}
      <div className="pt-6" />

      {/* Maturity Snapshot */}
      {activeTab === 'snapshot' && (
        <div className="space-y-5">
          {/* Hero metrics */}
          <div className="bg-gray-50 dark:bg-gray-800/30 rounded-xl p-5">
            <div className="grid grid-cols-3 gap-8">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Overall Maturity</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-[32px] font-bold" style={{ color: scoreColor }}>{programHealth.overallScore}%</span>
                  <span className="text-sm font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${scoreColor}15`, color: scoreColor }}>
                    {programHealth.maturityLevel?.label}
                  </span>
                </div>
                <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${programHealth.overallScore}%`, backgroundColor: scoreColor }} />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Benchmark Gap</p>
                <div className="flex items-baseline gap-2">
                  <span className={`text-[32px] font-bold ${programHealth.benchmark.gap >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {programHealth.benchmark.gap > 0 ? '+' : ''}{programHealth.benchmark.gap}%
                  </span>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {programHealth.benchmark.gap >= 0 ? 'Above' : 'Below'} {client?.industry || 'industry'} peers ({programHealth.benchmark.industry}% avg)
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Risk Appetite</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-[32px] font-bold text-gray-900 dark:text-white">{programHealth.riskAppetite.average}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{programHealth.riskAppetite.label}</span>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{categoryCount} domains assessed</p>
              </div>
            </div>
          </div>

          {/* Assessment Score Card with CMMI Level */}
          <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Navy gradient header */}
            <div className="p-5" style={{ background: 'linear-gradient(135deg, #012438, #213C4E)' }}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs uppercase tracking-wider" style={{ color: '#7E8F9C', letterSpacing: '1px' }}>
                    Security Assessment
                  </p>
                  <p className="text-lg font-bold text-white mt-1">Maturity Score: {programHealth.overallScore} / 100</p>
                </div>
                {currentLevel && (
                  <div className="text-center px-4 py-2 rounded-xl" style={{ background: currentLevel.color + '20' }}>
                    <p className="text-2xl font-extrabold leading-none" style={{ color: currentLevel.color }}>L{currentLevel.level}</p>
                    <p className="text-xs mt-1" style={{ color: '#BCC5CD' }}>{currentLevel.label}</p>
                  </div>
                )}
              </div>
            </div>

            {/* CMMI level bar */}
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1">
                {MATURITY_LEVELS.map(level => {
                  const isActive = currentLevel?.level === level.level
                  const isPast = currentLevel && level.level < currentLevel.level
                  return (
                    <div key={level.level} className="flex-1 flex flex-col items-center gap-1.5">
                      <div
                        className="h-1.5 w-full rounded-full"
                        style={{
                          backgroundColor: isPast || isActive ? level.color : '#F1F5F9',
                          opacity: isPast ? 0.4 : 1
                        }}
                      />
                      <p className="text-center" style={{ fontSize: '9px', fontWeight: isActive ? 700 : 400, color: isActive ? level.color : '#94A3B8' }}>
                        L{level.level}<br />{level.label}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Interpretive text */}
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <p className="text-sm font-semibold mb-1.5 text-gray-900 dark:text-white">What This Means for {client?.name || 'Your Organization'}</p>
              <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                At <strong className="text-gray-900 dark:text-white">CMMI Level {currentLevel?.level} ({currentLevel?.label})</strong>, your organization has
                {currentLevel?.level >= 4
                  ? ' moved beyond documented processes into actively measured and controlled security operations. Your controls are functioning, your team has defined responsibilities, and you have visibility into your security posture.'
                  : currentLevel?.level >= 3
                  ? ' documented and standardized security processes. Consistency is improving across your organization.'
                  : currentLevel?.level >= 2
                  ? ' basic security processes forming. There is room for significant improvement in formalizing controls.'
                  : ' reactive, ad hoc security processes. Establishing foundational controls should be the immediate priority.'
                }
                {percentile != null && (
                  <> You are performing in the <strong className="text-gray-900 dark:text-white">{Math.round(percentile)}th percentile</strong> among {industry.toLowerCase()} organizations of similar size.</>
                )}
              </p>
            </div>

            {/* Peer comparison bar */}
            {percentile != null && (
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <p className="text-xs font-semibold mb-3 text-gray-900 dark:text-white">Industry Peer Comparison</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="relative h-6 rounded-full bg-gray-50 dark:bg-gray-800/50">
                      <div
                        className="absolute left-0 top-0 h-full rounded-full"
                        style={{ width: `${percentile}%`, background: 'linear-gradient(90deg, #012438, #213C4E)' }}
                      />
                      <div
                        className="absolute top-[-4px] w-0.5 h-8 bg-primary-400"
                        style={{ left: `${percentile}%`, transform: 'translateX(-50%)' }}
                      />
                      <div
                        className="absolute top-[-4px] w-0.5 h-8 bg-gray-200 dark:bg-gray-700"
                        style={{ left: '50%', transform: 'translateX(-50%)' }}
                      />
                    </div>
                    <div className="flex justify-between mt-1.5">
                      <span className="text-xs text-gray-400 dark:text-gray-500">0th percentile</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Median</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">100th percentile</span>
                    </div>
                  </div>
                  <div className="text-center flex-shrink-0 w-20">
                    <p className="text-2xl font-extrabold text-gray-900 dark:text-white">{Math.round(percentile)}th</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Percentile</p>
                  </div>
                </div>
                <p className="text-xs mt-2 text-gray-400 dark:text-gray-500">
                  Compared to: {industry} organizations of similar size and regulatory scope
                </p>
              </div>
            )}

          </div>

          {/* Strengths & Gaps side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <InsightCard title="Strongest Domains" accent="#22C55E">
              <div className="space-y-4">
                {strongest.map(cat => (
                  <div key={cat.name} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-12 text-right">
                      <span className="text-lg font-bold text-green-600 dark:text-green-400">{cat.score}%</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{cat.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {DOMAIN_CONTEXT[cat.name]?.strength || 'Strong performance in this domain.'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </InsightCard>
            <InsightCard title="Priority Gaps" accent="#EF4444">
              <div className="space-y-4">
                {weakest.map(cat => (
                  <div key={cat.name} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-12 text-right">
                      <span className={`text-lg font-bold ${cat.score < 40 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>{cat.score}%</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{cat.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {DOMAIN_CONTEXT[cat.name]?.weakness || 'This domain requires attention.'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </InsightCard>
          </div>
        </div>
      )}

      {/* Domain Health — full Domain Analysis */}
      {activeTab === 'domains' && (
        <DomainAnalysis assessmentId={assessmentId} client={client} embedded />
      )}

    </div>
  )
}

export default ProgramOverview
